import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Typography } from 'antd';

import { taskService } from '../../../../modules/api-client/services/v1/TaskService';
import { RESPONSE_STATUS_ERROR } from '../../../../modules/api-client/services/interfaces';
import { ActionButton } from '../../../../components/ActionButton';
import { Modal } from '../../../../components/Modal';
import { useGlobalErrorStore } from '../../../../components/GlobalErrorBanner/store';
import { extractBackendErrorMessage } from '../../../../helpers/extractBackendErrorMessage';
import { callbackRequestSchema, CallbackRequestFormValues } from './schema';

import styles from './styles.module.css';

const { Text } = Typography;
const { TextArea } = Input;

const GENERIC_ERROR_MESSAGE = 'Не удалось отправить запрос. Попробуйте ещё раз.';

interface Props {
    taskId: number;
    onClose: () => void;
    onSuccess: () => void;
}

export const CallbackRequestModal: FC<Props> = ({ taskId, onClose, onSuccess }) => {
    const showGlobalError = useGlobalErrorStore((state) => state.showError);
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<CallbackRequestFormValues>({
        resolver: zodResolver(callbackRequestSchema),
        defaultValues: { senderFullName: '', shopCity: '', comment: '' },
    });
    const formValues = watch();
    const commentLength = formValues.comment?.length ?? 0;
    const hasAllFieldsFilled = Boolean(formValues.senderFullName) && Boolean(formValues.shopCity) && Boolean(formValues.comment);

    const onSubmit = async (values: CallbackRequestFormValues) => {
        try {
            const envelope = await taskService.requestCallback({ id: taskId, ...values });
            if (envelope.status === RESPONSE_STATUS_ERROR) {
                showGlobalError(envelope.messages[0]?.message ?? GENERIC_ERROR_MESSAGE);
                return;
            }
            onSuccess();
        } catch (error) {
            console.error(error);
            showGlobalError(extractBackendErrorMessage(error) ?? GENERIC_ERROR_MESSAGE);
        }
    };

    return (
        <Modal
            title={'Запрос на обратный звонок'}
            onClose={onClose}
            maskClosable={!isSubmitting}
            closable={!isSubmitting}
            footer={
                <div className={styles.footer}>
                    <ActionButton onClick={onClose} disabled={isSubmitting}>
                        Отменить
                    </ActionButton>
                    <ActionButton
                        type={'primary'}
                        htmlType={'submit'}
                        loading={isSubmitting}
                        disabled={!hasAllFieldsFilled}
                        onClick={handleSubmit(onSubmit)}
                    >
                        Отправить
                    </ActionButton>
                </div>
            }
        >
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <div className={styles.field}>
                    <Text className={styles.label}>ФИО сотрудника</Text>
                    <Controller
                        control={control}
                        name={'senderFullName'}
                        render={({ field }) => (
                            <Input
                                {...field}
                                status={errors.senderFullName ? 'error' : undefined}
                                disabled={isSubmitting}
                                className={styles.input}
                            />
                        )}
                    />
                    {errors.senderFullName && (
                        <Text className={styles.error}>{errors.senderFullName.message}</Text>
                    )}
                </div>

                <div className={styles.field}>
                    <Text className={styles.label}>Город магазина</Text>
                    <Controller
                        control={control}
                        name={'shopCity'}
                        render={({ field }) => (
                            <Input
                                {...field}
                                status={errors.shopCity ? 'error' : undefined}
                                disabled={isSubmitting}
                                className={styles.input}
                            />
                        )}
                    />
                    {errors.shopCity && <Text className={styles.error}>{errors.shopCity.message}</Text>}
                </div>

                <div className={styles.field}>
                    <Text className={styles.label}>Комментарий</Text>
                    <Controller
                        control={control}
                        name={'comment'}
                        render={({ field }) => (
                            <TextArea
                                {...field}
                                placeholder={'Опишите суть обращения клиента'}
                                maxLength={3000}
                                rows={4}
                                status={errors.comment ? 'error' : undefined}
                                disabled={isSubmitting}
                                className={styles.input}
                            />
                        )}
                    />
                    <div className={styles.commentFooter}>
                        {errors.comment ? (
                            <Text className={styles.error}>{errors.comment.message}</Text>
                        ) : (
                            <span />
                        )}
                        <Text className={styles.counter}>{commentLength} / 3000</Text>
                    </div>
                </div>
            </form>
        </Modal>
    );
};
